import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields } from "./MaxCopayScheduleStepsFormConfig";

const screenInfo = {
  ADD: ["Add Max Copay Schedule Step", "RCMCZ002"],
  CHANGE: ["Max Copay Schedule Step Detail", "RCMCZ003"],
  VIEW: ["Max Copay Schedule Step Detail", "RCMCZ003"],
};

const MaxCopayScheduleStepsForm = ({ mode }) => {
  const location = useLocation();
  const { axios_helper_no_header } = useAxios();
  const { state: stateVal } = location;
  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  console.log("current state in form", currentState, previousState);
  const {
    selectedSchedules = [],
    selectedSteps = [],
    selectedType = "1",
    previousScreen = ROUTE_PATHS.MAX_COPAY_SCHEDULE_STEPS,
    currentIndex: initialIndex = 0,
  } = currentState;
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const handleBack = () => {
    if (currentIndex < selectedSteps.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      const updatedPreviousState = previousState.map((state, index) => {
        if (index === previousState.length - 1) {
          return {
            ...state,
            paramsData: {
              ...state.paramsData,
              mczDaysSupply: currentState.selectedSteps[currentIndex]?.mczDaysSupply || "",
            },
          };
        }
        return state;
      });
      navigateToPage(previousScreen, updatedPreviousState);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitFormData = (data) => {
    setDataForSubmission(data);
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/mczMaxCopaySteps";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/mczMaxCopaySteps";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        const updatedCurrentState = {
          ...currentState,
          selectedSteps: [
            {
              ...responseData,
              mcyMaxCopayDescription: selectedSchedules[0].mcyMaxCopayDescription,
              mczDaysSupply: responseData.mczDaysSupply, 
            },
          ],
          selectedSchedules,
          currentIndex: 0,
        };
        navigateToPage(ROUTE_PATHS.EDIT_MAX_COPAY_SCHEDULE_STEPS, [
          ...previousState,
          updatedCurrentState,
        ]);
      }
    // setInitialFormData(responseData);
      /*setInitialFormData({
        ...responseData,
        mcyMaxCopayDescription:
          selectedSchedules[initialIndex].mcyMaxCopayDescription,
      });*/
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
    } catch (error) {
      const { responseStatus: { displayErrorFields = null, message } = {} } =
        error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex]);

  useEffect(() => {
    const paramsData = {
      mcyMaxCopaySchedule: selectedSchedules[currentIndex]?.mcyMaxCopaySchedule,
      mcyMaxCopayDescription: currentState?.mcyMaxCopayDescription,
      mczMaxCopayType: selectedType,
      mczDaysSupply: selectedSteps[currentIndex].mczDaysSupply,
    };
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? `api/mczMaxCopaySteps/initialFormData/${screenInfo.ADD[1]}`
        : `api/mczMaxCopaySteps/${encodeURIComponent(
            selectedSchedules[0].mcyMaxCopaySchedule
          )}`;
      const { responseData } = await axios_helper_no_header(
        "GET",
        url,
        {},
        paramsData
      );
      // setInitialFormData(responseData);
      setInitialFormData({
        ...responseData,
        mcyMaxCopayDescription:
         currentState.mcyMaxCopayDescription,
      });
    };

    fetchInitialData();
  }, [mode, currentIndex]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
    </div>
  );
};

export default MaxCopayScheduleStepsForm;
